import React, { useMemo, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import DatasetDialog, { Field, inputClass, textareaClass, primaryClass, neutralClass } from 'features/datasets/components/DatasetDialog';
import { discoverFieldSchema, newDerivedId } from 'features/datasets/utils/derivedColumnEvaluator';

const EXPR_EXAMPLES = [
  { label: 'Query length', expr: 'LENGTH(input.query)' },
  { label: 'Category uppercase', expr: 'UPPER(metadata.category)' },
  { label: 'Query and expected', expr: 'input.query || " -> " || expected_output' },
];

const MODES = [{ id: 'path', label: 'Field' }, { id: 'expr', label: 'Expression' }];

/**
 * A derived column: a read-only column computed from each row's input,
 * expected output or metadata, either a single field or an expression.
 * Opening an existing one edits it, and offers to delete it.
 */
export default function DerivedColumnModal({
  onClose, onSave, onDelete, existing, rows, inputColumns, reservedNames = [],
}) {
  const editing = Boolean(existing?.id);
  const [name, setName] = useState(existing?.name || '');
  const [mode, setMode] = useState(existing?.mode || 'path');
  const [path, setPath] = useState(existing?.path || '');
  const [expr, setExpr] = useState(existing?.expr || '');
  const [err, setErr] = useState(null);

  const schema = useMemo(() => discoverFieldSchema(rows, inputColumns), [rows, inputColumns]);
  const fieldOptions = useMemo(() => {
    const opts = [];
    for (const k of schema.input) opts.push({ value: `input.${k}`, label: `input.${k}`, hint: 'Input' });
    for (const k of schema.metadata) opts.push({ value: `metadata.${k}`, label: `metadata.${k}`, hint: 'Metadata' });
    if (schema.has_expected) opts.push({ value: 'expected_output', label: 'expected_output', hint: 'Expected' });
    return opts;
  }, [schema]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) { setErr('Name is required.'); return; }
    if (reservedNames.includes(trimmed)) { setErr('A column already has this name.'); return; }
    if (mode === 'path' && !path) { setErr('Choose a field.'); return; }
    if (mode === 'expr' && !expr.trim()) { setErr('Enter an expression.'); return; }
    onSave({
      id: existing?.id || newDerivedId(),
      name: trimmed,
      mode,
      path: mode === 'path' ? path : undefined,
      expr: mode === 'expr' ? expr.trim() : undefined,
    });
    onClose();
  };

  return (
    <DatasetDialog title={editing ? 'Edit derived column' : 'Derived column'} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          {editing ? <button type="button" onClick={() => { onDelete?.(existing.id); onClose(); }} className={`${neutralClass} mr-auto hover:!text-danger`}>Delete</button> : null}
          <button type="button" onClick={onClose} className={neutralClass}>Cancel</button>
          <button type="submit" className={primaryClass}>{editing ? 'Save' : 'Add'}</button>
        </>
      )}>
      <Field label="Name" htmlFor="ds-derived-name">
        <input id="ds-derived-name" autoFocus value={name} onChange={(e) => { setName(e.target.value); setErr(null); }} placeholder="Reference" className={inputClass} />
      </Field>

      <div className="flex items-center gap-4 border-b border-[color:var(--border-subtle)]" role="tablist" aria-label="Source">
        {MODES.map((m) => (
          <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} onClick={() => { setMode(m.id); setErr(null); }}
            className={`-mb-px border-b-2 pb-1.5 text-[12px] font-medium ${mode === m.id ? 'border-[color:var(--text-primary)] text-[color:var(--text-primary)]' : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'path' ? (
        <Field label="Field">
          <BrandDropdown value={path} onChange={(v) => { setPath(v); setErr(null); }} options={fieldOptions} compact triggerClassName="w-full"
            placeholder={fieldOptions.length ? 'Choose a field' : 'No fields in the rows yet'} ariaLabel="Field" disabled={!fieldOptions.length}
            renderOption={(o) => (<span className="flex w-full items-center gap-2"><span className="font-mono text-[11.5px]">{o.label}</span><span className="ml-auto text-[10.5px] text-[color:var(--text-muted)]">{o.hint}</span></span>)} />
        </Field>
      ) : (
        <div className="flex flex-col gap-2">
          <Field label="Expression" htmlFor="ds-derived-expr">
            <textarea id="ds-derived-expr" value={expr} onChange={(e) => { setExpr(e.target.value); setErr(null); }} spellCheck={false} rows={3}
              placeholder="LENGTH(input.query)" className={`${textareaClass} font-mono text-[11.5px]`} />
          </Field>
          <div className="flex flex-wrap gap-1.5">
            {EXPR_EXAMPLES.map((ex) => (
              <button key={ex.expr} type="button" onClick={() => setExpr(ex.expr)}
                className="rounded-md border border-[color:var(--border-default)] px-2 py-0.5 text-[11.5px] text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                {ex.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {err ? <p className="text-[12px] text-danger">{err}</p> : null}
    </DatasetDialog>
  );
}
